import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';
import {
  Calendar,
  Col,
  DatePicker,
  Divider,
  Form,
  Row,
  Skeleton,
  Space,
  Statistic,
} from 'antd';

import { PageProps } from '@/types/PageProps';

import DefaultRouteTabs from '@/components/_common/DefaultRouteTabs';
import moment, { Moment } from 'moment';
import { useCallback, useState } from 'react';
import { useAccountPayableRangeDate } from '@/core/domain/financial/AccountPayable/AccountPayable.hooks';
import { DateTimeFormat } from '@/utils/moment';
import StatusTag from '@/components/_common/StatusTag';
import {
  AccountPayableRangeDateResponse,
  AccountPayableSituationColor,
  InstallmentSituation,
  InstallmentSituationType,
} from '@/core/domain/financial/AccountPayable/AccountPayable.types';
import { useDisclosure, useWindowSize } from '@/hooks';
import CalendarDetailPayableModal from '@/components/Registry/BillToPay/ViewCalendar/CalendarDetailPayableModal';
import AgendaList, { AgendaData } from '@/components/_common/AgendaList';
import formatterPrice from '@/utils/number/formatterPrice';
import {
  CompaniesInfiniteSelectField,
  FloatingFormItem,
  Group,
} from '@/components/_common';

function ViewCalendarAccountPayablePage({ route }: PageProps) {
  const intl = useIntl();
  const [form] = Form.useForm();

  const [date, setDate] = useState<Moment>(moment());
  const [companyId, setCompanyId] = useState();
  const [accountSelect, setAccountSelect] =
    useState<AccountPayableRangeDateResponse>();
  const { isMobile } = useWindowSize();

  const [modalVisible, modalVisibleAction] = useDisclosure();

  const accountPayables = useAccountPayableRangeDate({
    initialDate: date.startOf('month').format(DateTimeFormat),
    finalDate: date.endOf('month').format(DateTimeFormat),
    companyId,
  });

  const items = accountPayables.data || [];

  const handleSelectItem = (accountSelect: AccountPayableRangeDateResponse) => {
    if (accountSelect) {
      setAccountSelect(accountSelect);

      modalVisibleAction.open();
    }
  };

  const dateCellRender = useCallback(
    (value: Moment) => {
      if (accountPayables.isFetching) {
        return (
          <Space wrap>
            <Skeleton.Input style={{ width: '100%', height: 20 }} />
            <Skeleton.Input style={{ width: '100%', height: 20 }} />
          </Space>
        );
      }

      if (value.isBefore(date, 'month') || value.isAfter(date, 'month')) {
        return null;
      }

      return (
        <Space wrap>
          {!!items &&
            items
              ?.filter((item) => value.isSame(moment(item.dueDate), 'day'))
              .map((item) => (
                <StatusTag
                  key={item.id}
                  label={`${item.accountPayableCode}/${
                    item.installmentNumber
                  } - ${InstallmentSituationType[item.situation]}`}
                  color={AccountPayableSituationColor[item.situation]}
                  onClick={() => handleSelectItem(item)}
                />
              ))}
        </Space>
      );
    },
    [accountPayables.dataUpdatedAt],
  );

  function agendaTag(item: AccountPayableRangeDateResponse) {
    return (
      <StatusTag
        key={item.id}
        label={`${item.accountPayableCode}/${item.installmentNumber} - ${
          InstallmentSituationType[item.situation]
        }`}
        color={AccountPayableSituationColor[item.situation]}
        onClick={() => handleSelectItem(item)}
        style={{ width: '100%' }}
        size="lg"
      />
    );
  }

  const getAgendaData = useCallback(() => {
    return (
      items?.reduce<AgendaData[]>((acc, item) => {
        const isSameDay = (date: Moment) =>
          moment(item.dueDate).isSame(date, 'day');

        if (acc.find((accItem) => isSameDay(accItem.date))) {
          return acc.map((accItem) => {
            if (isSameDay(accItem.date)) {
              return {
                ...accItem,
                render: [...accItem.render, agendaTag(item)],
              };
            }
            return accItem;
          });
        }

        return [
          ...acc,
          { date: moment(item.dueDate), render: [agendaTag(item)] },
        ];
      }, [] as AgendaData[]) || []
    );
  }, [items]);

  return (
    <>
      <DefaultRouteTabs route={route.parent} permissions={route.permissions}>
        <Helmet title={intl.formatMessage({ id: route.name })} />

        <Row gutter={[24, 24]} style={{ marginBottom: '24px' }}>
          <Col>
            <Statistic
              title="Total de parcelas abertas"
              value={items
                ?.filter(
                  (item) => item.situation !== InstallmentSituation.settled,
                )
                .reduce(
                  (acc, cur) =>
                    (acc += cur.installmentValue + (cur.discountIncrease || 0)),
                  0,
                )}
              formatter={(value) => formatterPrice(Number(value))}
              valueStyle={{
                color: '#2b8a3e',
              }}
            />
          </Col>

          <Col>
            <Divider type="vertical" style={{ minHeight: '100%' }} />
          </Col>

          <Col>
            <Statistic
              title="Total de parcelas liquidadas"
              value={items
                ?.filter((item) => item.situation !== InstallmentSituation.open)
                ?.reduce(
                  (acc, cur) =>
                    (acc += cur.installmentValue + (cur.discountIncrease || 0)),
                  0,
                )}
              formatter={(value) => formatterPrice(Number(value))}
              valueStyle={{
                color: '#1864ab',
              }}
            />
          </Col>
        </Row>

        {isMobile && (
          <AgendaList
            dataSource={getAgendaData()}
            onChange={setDate}
            date={date}
          />
        )}

        {!isMobile && (
          <Calendar
            key={accountPayables.dataUpdatedAt}
            value={date}
            dateCellRender={dateCellRender}
            mode="month"
            onChange={setDate}
            headerRender={(config) => (
              <Form form={form}>
                <Row gutter={[16, 16]}>
                  <Col span={24} md={12} lg={12}>
                    <FloatingFormItem label="Empresa" name="companyId">
                      <CompaniesInfiniteSelectField
                        value={companyId}
                        onChange={(value) => {
                          setCompanyId(value);
                        }}
                        allowClear
                      />
                    </FloatingFormItem>
                  </Col>

                  <Col span={24} md={12} lg={12}>
                    <Group position="right">
                      <div>
                        <small style={{ display: 'block' }}>
                          Selecionar mês:
                        </small>

                        <DatePicker.MonthPicker
                          value={config.value}
                          onChange={(inputValue) =>
                            config.onChange(inputValue || moment())
                          }
                          format="MMM/YYYY"
                        />
                      </div>
                    </Group>
                  </Col>
                </Row>
              </Form>
            )}
          />
        )}

        <CalendarDetailPayableModal
          open={modalVisible}
          onCancel={modalVisibleAction.close}
          accountSelected={accountSelect}
        />
      </DefaultRouteTabs>
    </>
  );
}

export default ViewCalendarAccountPayablePage;
